import { defineStore, storeToRefs } from "pinia";
import type {
    DynamicDialogCloseOptions,
    DynamicDialogInstance,
} from "primevue/dynamicdialogoptions";
import { useDialog } from "primevue/usedialog";
import { computed, ref } from "vue";

import CreateReleaseDialog from "@/components/dialogs/CreateReleaseDialog.vue";
import ReleaseDiffDialog from "@/components/dialogs/ReleaseDiffDialog.vue";
import { useStekManager } from "@/composables/stek-manager";
import { useServices } from "@/lib/openapi";
import type {
    ReleaseDiffResponse,
    StekReleaseResponse,
} from "@/lib/openapi/gen/types.gen";

export type { StekReleaseResponse, ReleaseDiffResponse };

export interface CreateReleaseResult extends DynamicDialogCloseOptions {
    data: {
        success: boolean;
        release?: StekReleaseResponse;
    };
    error?: unknown;
}

export interface UnreleasedChanges {
    totalFiles: number;
    totalAdditions: number;
    totalDeletions: number;
    latestRelease: string;
}

export const useReleaseStore = defineStore("release", () => {
    const isLoading = ref(false);
    const error = ref<string>();
    const releases = ref<StekReleaseResponse[]>([]);
    const initialized = ref(false);
    const unreleasedChanges = ref<UnreleasedChanges | null>(null);

    // Active release state — shared between Design and Live modes
    const activeRelease = ref<string | null>(null);
    const activeRevision = computed(() =>
        activeRelease.value ? `releases/${activeRelease.value}` : null
    );

    const setActiveRelease = (name: string | null) => {
        activeRelease.value = name;
    };

    const clearActiveRelease = () => {
        activeRelease.value = null;
    };

    /**
     * Reset all release state when switching to a different stek.
     * Prevents stale releases from a previous stek persisting into the new one.
     */
    const resetForNewStek = () => {
        activeRelease.value = null;
        releases.value = [];
        initialized.value = false;
        unreleasedChanges.value = null;
    };

    // Dialog related state — useDialog() requires Vue's inject() context,
    // so it must be initialized during a component's setup() phase via initDialog().
    let dialog: ReturnType<typeof useDialog> | null = null;
    const initDialog = () => {
        if (!dialog) {
            dialog = useDialog();
        }
    };
    const getDialog = () => {
        if (!dialog) {
            throw new Error(
                "Dialog not initialized. Call releaseStore.initDialog() during component setup()."
            );
        }
        return dialog;
    };
    const dialogRef = ref<DynamicDialogInstance & { visible?: boolean }>();
    const isDialogVisible = computed(() => Boolean(dialogRef.value?.visible));

    // Use the SDK with the proper requirements (needs stek context)
    const api = useServices({
        opts: {
            throwOnError: false,
        },
        requires: {
            auth: true,
            organization: true,
            stek: true,
        },
    });

    const checkUnreleasedChanges = async () => {
        const { stek } = useStekManager();
        if (!stek.value || !releases.value.length) {
            unreleasedChanges.value = null;
            return;
        }

        const latestRelease = releases.value[0].name;
        try {
            const { response, data } =
                await api.startStekStekReleaseMgtGetReleaseDiffStartPost({
                    body: {
                        to_release: latestRelease,
                        from_release: undefined,
                    },
                });

            if (!response?.ok || !data) {
                unreleasedChanges.value = null;
                return;
            }

            unreleasedChanges.value =
                data.total_files_changed > 0
                    ? {
                          totalFiles: data.total_files_changed,
                          totalAdditions: data.total_additions,
                          totalDeletions: data.total_deletions,
                          latestRelease,
                      }
                    : null;
        } catch (err) {
            console.error("Failed to check unreleased changes:", err);
            unreleasedChanges.value = null;
        }
    };

    const listReleases = async () => {
        const { stek } = useStekManager();
        if (!stek.value) {
            releases.value = [];
            return [];
        }

        isLoading.value = true;
        error.value = undefined;

        try {
            const { response, data } =
                await api.startStekStekReleaseMgtListReleasesStartPost({});

            if (!response?.ok) {
                throw new Error(
                    `Failed to fetch releases: ${response?.status || "unknown"}`
                );
            }

            releases.value = data || [];
            initialized.value = true;
            // Always recheck unreleased changes after fetching releases
            checkUnreleasedChanges();
            return releases.value;
        } catch (err) {
            console.error("Failed to fetch releases:", err);
            error.value = "Failed to load releases. Please try again.";
            return [];
        } finally {
            isLoading.value = false;
        }
    };

    const createRelease = async (name: string, description?: string) => {
        isLoading.value = true;
        error.value = undefined;

        try {
            const { response, data } =
                await api.startStekStekReleaseMgtCreateReleaseStartPost({
                    body: {
                        name: name,
                        description: description,
                    },
                });

            if (!response) {
                // Network error or request failed
                throw new Error(
                    "Failed to create release: No response received"
                );
            } else if (!response.ok) {
                console.error("Release creation response error:", {
                    status: response.status,
                    statusText: response.statusText,
                });
                throw new Error(`Failed to create release: ${response.status}`);
            }

            initialized.value = true;
            // Refresh the list after creating
            try {
                await listReleases();
            } catch (e) {
                console.error("Error refreshing releases after create:", e);
            }

            return data;
        } catch (err) {
            console.error("Failed to create release:", err);
            error.value = "Failed to create release. Please try again.";
            return;
        } finally {
            isLoading.value = false;
        }
    };

    const getRelease = async (releaseName: string) => {
        isLoading.value = true;
        error.value = undefined;

        try {
            const { response, data } =
                await api.startStekStekReleaseMgtGetReleaseStartPost({
                    body: {
                        release_name: releaseName,
                    },
                });

            if (!response?.ok) {
                throw new Error(
                    `Failed to fetch release: ${response?.status || "unknown"}`
                );
            }

            return data;
        } catch (err) {
            console.error("Failed to fetch release:", err);
            error.value = "Failed to load release. Please try again.";
            return;
        } finally {
            isLoading.value = false;
        }
    };

    const getReleaseDiff = async (
        toRelease: string,
        fromRelease?: string
    ): Promise<ReleaseDiffResponse | undefined> => {
        isLoading.value = true;
        error.value = undefined;

        try {
            const { response, data } =
                await api.startStekStekReleaseMgtGetReleaseDiffStartPost({
                    body: {
                        to_release: toRelease,
                        from_release: fromRelease,
                    },
                });

            if (!response?.ok) {
                throw new Error(
                    `Failed to fetch release diff: ${response?.status || "unknown"}`
                );
            }

            return data;
        } catch (err) {
            console.error("Failed to fetch release diff:", err);
            error.value = "Failed to load release diff. Please try again.";
            return;
        } finally {
            isLoading.value = false;
        }
    };

    // Dialog functions
    const openCreateReleaseDialog = () => {
        dialogRef.value = getDialog().open(CreateReleaseDialog, {
            props: {
                header: "Create New Release",
                style: {
                    width: "520px",
                },
                modal: true,
                dismissableMask: true,
                closeOnEscape: true,
            },
            onClose: (result?: CreateReleaseResult) => {
                if (result?.data?.success) {
                    // Refresh the list after dialog closes successfully
                    listReleases();
                }
            },
        });

        return dialogRef.value;
    };

    const openReleaseDiffDialog = (toRelease: string, fromRelease?: string) => {
        dialogRef.value = getDialog().open(ReleaseDiffDialog, {
            props: {
                header: `Changes${fromRelease ? ` from ${fromRelease}` : ""} to ${toRelease}`,
                style: {
                    width: "80vw",
                    maxWidth: "1200px",
                },
                modal: true,
                dismissableMask: true,
                closeOnEscape: true,
            },
            data: {
                toRelease,
                fromRelease,
            },
        });

        return dialogRef.value;
    };

    return {
        releases,
        isLoading,
        error,
        initialized,
        unreleasedChanges,
        activeRelease,
        activeRevision,
        setActiveRelease,
        clearActiveRelease,
        resetForNewStek,
        listReleases,
        createRelease,
        getRelease,
        getReleaseDiff,
        checkUnreleasedChanges,
        initDialog,
        openCreateReleaseDialog,
        openReleaseDiffDialog,
        isDialogVisible,
        refresh: () => listReleases(),
    };
});

export const useReleaseManager = () => storeToRefs(useReleaseStore());
